import axios from 'axios';
import localforage from 'localforage';

// Configuration des stores
const logStore = localforage.createInstance({ name: 'clientLogs' });

// Niveau de log et configuration
const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  CRITICAL: 4
};

// Catégories de logs
const LOG_CATEGORIES = {
  DEFAULT: 'default',       // Catégorie par défaut
  SYNC_DATA: 'sync_data',   // Logs liés à la synchronisation des données
  APP_STATE: 'app_state',   // Logs liés à l'état de l'application
  NETWORK: 'network',       // Logs liés au réseau
  USER_ACTION: 'user_action'// Logs liés aux actions utilisateur
};

// Configuration par défaut
const DEFAULT_CONFIG = {
  // Niveau minimum de log à enregistrer (DEBUG inclut tous les logs)
  minLevel: LOG_LEVELS.DEBUG,
  // Niveau minimum pour envoyer les logs au serveur
  minServerLevel: LOG_LEVELS.INFO,
  // Catégories à toujours envoyer au serveur, quel que soit le niveau
  priorityCategories: [LOG_CATEGORIES.SYNC_DATA, LOG_CATEGORIES.ERROR],
  // URL de l'API où envoyer les logs
  apiEndpoint: '/livraison/logs/appDelivery/',
  // Intervalle de synchronisation automatique en minutes
  syncInterval: 5,
  // Capacité maximale de logs stockés hors ligne
  maxStoredLogs: 1000,
  // Durée de conservation des logs en jours
  retentionDays: 7,
  // Nombre de logs à envoyer par lot
  batchSize: 50,
  // Activer la capture des erreurs non gérées
  captureUnhandledErrors: true,
  // Inclure les métadonnées supplémentaires
  includeMetadata: true,
  // Afficher les logs dans la console (en dev)
  consoleOutput: process.env.NODE_ENV !== 'production'
};

class LogService {
  constructor() {
    this.config = {...DEFAULT_CONFIG};
    this.syncInProgress = false;
    this.autoSyncTimer = null;
    this.queue = [];
    this.memoryBuffer = [];
    this.metadata = {};
    this.originalConsole = {
      log: console.log,
      info: console.info,
      warn: console.warn,
      error: console.error,
      debug: console.debug
    };
  }

  /**
   * Initialise le service de logging avec la configuration fournie
   * @param {Object} config - Configuration du service
   */
  init(config = {}) {
    // Fusionner la configuration par défaut avec celle fournie
    this.config = {...DEFAULT_CONFIG, ...config};
    
    // Collecter les métadonnées système
    this.collectMetadata();
    
    // Installer les gestionnaires d'erreurs globaux
    if (this.config.captureUnhandledErrors) {
      this.setupErrorHandlers();
    }
    
    // Installer les écouteurs d'état réseau
    this.setupNetworkListeners();
    
    // Démarrer la synchronisation automatique
    this.startAutoSync();
    
    // Surcharger console.log et autres méthodes de console
    this.overrideConsole();
    
    // Log initial pour confirmer l'initialisation
    this.info('LogService initialized', { config: this.config });
    
    return this;
  }

  /**
   * Remplace les méthodes console natives par les nôtres pour capturer tous les logs
   */
  overrideConsole() {
    if (!this.config.overrideConsole) return;
    
    console.log = (...args) => {
      this.originalConsole.log(...args);
      this.debug(this.formatConsoleArgs(args), { source: 'console.log' });
    };
    
    console.info = (...args) => {
      this.originalConsole.info(...args);
      this.info(this.formatConsoleArgs(args), { source: 'console.info' });
    };
    
    console.warn = (...args) => {
      this.originalConsole.warn(...args);
      this.warn(this.formatConsoleArgs(args), { source: 'console.warn' });
    };
    
    console.error = (...args) => {
      this.originalConsole.error(...args);
      this.error(this.formatConsoleArgs(args), { source: 'console.error' });
    };
    
    console.debug = (...args) => {
      this.originalConsole.debug(...args);
      this.debug(this.formatConsoleArgs(args), { source: 'console.debug' });
    };
  }

  /**
   * Formate les arguments de console pour le stockage
   */
  formatConsoleArgs(args) {
    return args.map(arg => {
      if (arg instanceof Error) {
        return {
          message: arg.message,
          stack: arg.stack,
          name: arg.name
        };
      } else if (typeof arg === 'object') {
        try {
          return JSON.stringify(arg);
        } catch {
          return '[Objet circulaire ou non-sérialisable]';
        }
      }
      return arg;
    }).join(' ');
  }

  /**
   * Restaure les méthodes de console d'origine
   */
  restoreConsole() {
    console.log = this.originalConsole.log;
    console.info = this.originalConsole.info;
    console.warn = this.originalConsole.warn;
    console.error = this.originalConsole.error;
    console.debug = this.originalConsole.debug;
  }

  /**
   * Collecte les métadonnées de l'environnement client
   */
  collectMetadata() {
    this.metadata = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      screenSize: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      devicePixelRatio: window.devicePixelRatio,
      platform: navigator.platform,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      referrer: document.referrer,
      connection: navigator.connection 
        ? {
            effectiveType: navigator.connection.effectiveType,
            downlink: navigator.connection.downlink,
            rtt: navigator.connection.rtt,
            saveData: navigator.connection.saveData
          } 
        : null,
      clientId: this.getClientId()
    };
  }

  /**
   * Obtient ou crée un ID client unique persistant
   */
  getClientId() {
    let clientId = localStorage.getItem('logService_clientId');
    
    if (!clientId) {
      clientId = 'client_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('logService_clientId', clientId);
    }
    
    return clientId;
  }

  /**
   * Configure les écouteurs d'erreurs non gérées
   */
  setupErrorHandlers() {
    window.addEventListener('error', (event) => {
      this.critical('Uncaught error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error ? {
          stack: event.error.stack,
          name: event.error.name,
          message: event.error.message
        } : null
      });
      
      // Ne pas empêcher le comportement par défaut
      return false;
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.critical('Unhandled promise rejection', {
        reason: event.reason instanceof Error 
          ? {
              stack: event.reason.stack,
              name: event.reason.name,
              message: event.reason.message
            }
          : event.reason
      });
    });
  }

  /**
   * Configure les écouteurs d'état réseau
   */
  setupNetworkListeners() {
    window.addEventListener('online', () => {
      this.info('Network connection restored', { 
        timestamp: new Date().toISOString() 
      });
      
      // Synchroniser les logs en attente lors du retour en ligne
      this.syncLogs();
    });

    window.addEventListener('offline', () => {
      this.warn('Network connection lost', { 
        timestamp: new Date().toISOString() 
      });
    });
  }

  /**
   * Crée un objet log standard avec toutes les métadonnées
   */
  createLogEntry(level, message, data = {}, category = LOG_CATEGORIES.DEFAULT) {
    // Générer un ID unique pour ce log
    const logId = `log_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    
    // Capturer l'état de la pile d'appels pour le debugging
    const stackTrace = this.config.includeStack 
      ? new Error().stack.split('\n').slice(2).join('\n')
      : null;

    // Construire l'entrée de log
    const logEntry = {
      id: logId,
      level,
      levelName: Object.keys(LOG_LEVELS).find(key => LOG_LEVELS[key] === level),
      category,
      message,
      data,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      stack: stackTrace,
      synced: false
    };

    // Ajouter les métadonnées si configuré
    if (this.config.includeMetadata) {
      logEntry.metadata = { ...this.metadata };
      
      // Ajouter des informations sur la connexion réseau actuelle
      if (navigator.connection) {
        logEntry.metadata.connection = {
          effectiveType: navigator.connection.effectiveType,
          downlink: navigator.connection.downlink,
          rtt: navigator.connection.rtt,
          saveData: navigator.connection.saveData,
          online: navigator.onLine
        };
      } else {
        logEntry.metadata.online = navigator.onLine;
      }
    }

    return logEntry;
  }

  /**
   * Méthode générique pour enregistrer un log de n'importe quel niveau
   */
  async log(level, message, data = {}, category = LOG_CATEGORIES.DEFAULT, sendImmediately = true) {
    // Vérifier si le niveau est suffisant pour être enregistré
    if (level < this.config.minLevel) {
      return;
    }

    try {
      // Créer l'entrée de log
      const logEntry = this.createLogEntry(level, message, data, category);
      
      // Ajouter au buffer mémoire pour accès rapide aux logs récents
      this.memoryBuffer.push(logEntry);
      // Limiter la taille du buffer mémoire
      if (this.memoryBuffer.length > 100) {
        this.memoryBuffer.shift();
      }
      
      // Afficher dans la console en mode développement
      if (this.config.consoleOutput) {
        const levelName = Object.keys(LOG_LEVELS).find(key => LOG_LEVELS[key] === level);
        const consoleMethod = {
          DEBUG: 'debug',
          INFO: 'info',
          WARN: 'warn',
          ERROR: 'error',
          CRITICAL: 'error'
        }[levelName] || 'log';
        
        this.originalConsole[consoleMethod](`[${levelName}][${category}] ${message}`, data);
      }

      // Si connecté, essayer d'envoyer immédiatement uniquement si sendImmediately est true ou si c'est un log critique/erreur/prioritaire
      if (navigator.onLine && 
         (sendImmediately && (level >= LOG_LEVELS.ERROR || 
          this.config.priorityCategories.includes(category)))) {
        try {
          await this.sendLogToServer(logEntry);
          return;
        } catch (error) {
          // En cas d'échec, sauvegarder localement
          console.warn('Failed to send log immediately, storing locally', error);
        }
      }

      // Stocker le log en local
      await this.storeLog(logEntry);
      
      // Tenter une synchronisation si le nombre de logs en file d'attente dépasse le seuil
      // seulement si sendImmediately est true pour éviter des synchronisations trop fréquentes
      if (sendImmediately && this.queue.length >= this.config.batchSize) {
        this.syncLogs();
      }
    } catch (error) {
      console.error('Error in LogService.log:', error);
    }
  }

  /**
   * Stocke un log dans IndexedDB
   */
  async storeLog(logEntry) {
    try {
      // Ajouter à la file d'attente en mémoire pour traitement par lots
      this.queue.push(logEntry);
      
      // Stocker aussi dans IndexedDB pour persistance
      await logStore.setItem(logEntry.id, logEntry);
      
      // Si la file d'attente dépasse un seuil, purger les anciens logs
      if (this.queue.length > this.config.maxStoredLogs) {
        this.purgeOldLogs();
      }
    } catch (error) {
      console.error('Failed to store log:', error);
    }
  }

  /**
   * Envoie un log unique au serveur
   */
  async sendLogToServer(logEntry) {
    try {
      // Créer une instance axios directement ici pour éviter les dépendances circulaires
      const baseURL = import.meta.env.VITE_APP_BASE_URL || 'http://localhost:8000';
      
      const response = await axios.post(
        `${baseURL}${this.config.apiEndpoint}`, 
        logEntry
      );
      
      if (response.status >= 200 && response.status < 300) {
        // Log envoyé avec succès, le marquer comme synchronisé
        logEntry.synced = true;
        await logStore.setItem(logEntry.id, logEntry);
        
        return true;
      }
      
      throw new Error(`Server responded with status ${response.status}`);
    } catch (error) {
      console.error('Error sending log to server:', error);
      throw error;
    }
  }

  /**
   * Synchronise les logs stockés localement avec le serveur
   */
  async syncLogs() {
    // Ne pas démarrer une nouvelle synchro si une est déjà en cours
    if (this.syncInProgress || !navigator.onLine) {
      return;
    }
    
    this.syncInProgress = true;
    
    try {
      // Récupérer tous les logs non synchronisés
      const keys = await logStore.keys();
      let unsyncedLogs = [];
      
      for (const key of keys) {
        const log = await logStore.getItem(key);
        if (log && !log.synced && 
           (log.level >= this.config.minServerLevel || 
            this.config.priorityCategories.includes(log.category))) {
          unsyncedLogs.push(log);
        }
      }
      
      // Limiter le nombre de logs par lot pour éviter des requêtes trop volumineuses
      unsyncedLogs = unsyncedLogs.slice(0, this.config.batchSize);
      
      if (unsyncedLogs.length === 0) {
        this.syncInProgress = false;
        return;
      }
      
      // Créer une instance axios directement ici pour éviter les dépendances circulaires
      const baseURL = import.meta.env.VITE_APP_BASE_URL || 'http://localhost:8000';
      
      // Envoyer les logs par lot
      const response = await axios.post(
        `${baseURL}${this.config.apiEndpoint}batch/`, 
        { logs: unsyncedLogs }
      );
      
      if (response.status >= 200 && response.status < 300) {
        // Marquer les logs comme synchronisés
        for (const log of unsyncedLogs) {
          log.synced = true;
          await logStore.setItem(log.id, log);
          
          // Supprimer de la file d'attente en mémoire
          const queueIndex = this.queue.findIndex(item => item.id === log.id);
          if (queueIndex !== -1) {
            this.queue.splice(queueIndex, 1);
          }
        }
        
        this.info(`Synchronized ${unsyncedLogs.length} logs with server`);
      }
    } catch (error) {
      console.error('Error during log synchronization:', error);
      this.error('Failed to synchronize logs', { error: error.message });
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Supprime les logs les plus anciens pour éviter un stockage excessif
   */
  async purgeOldLogs() {
    try {
      const keys = await logStore.keys();
      
      // Récupérer tous les logs avec leurs timestamps
      const logsWithTimestamps = [];
      for (const key of keys) {
        const log = await logStore.getItem(key);
        if (log) {
          logsWithTimestamps.push({
            id: log.id,
            timestamp: new Date(log.timestamp),
            synced: log.synced
          });
        }
      }
      
      // Trier par date (les plus anciens d'abord)
      logsWithTimestamps.sort((a, b) => a.timestamp - b.timestamp);
      
      // Calculer le nombre de logs à supprimer (en conservant de la marge)
      const logsToKeep = Math.floor(this.config.maxStoredLogs * 0.8);
      const logsToRemove = logsWithTimestamps.length - logsToKeep;
      
      if (logsToRemove <= 0) {
        return;
      }
      
      // Prioriser la suppression des logs déjà synchronisés
      const syncedLogs = logsWithTimestamps.filter(log => log.synced);
      
      // Supprimer d'abord les logs synchronisés anciens
      let removed = 0;
      for (const log of syncedLogs) {
        if (removed >= logsToRemove) break;
        
        await logStore.removeItem(log.id);
        removed++;
      }
      
      // Si pas assez de logs synchronisés supprimés, supprimer les plus anciens non-synchronisés
      if (removed < logsToRemove) {
        const remainingToRemove = logsToRemove - removed;
        const unsyncedLogs = logsWithTimestamps.filter(log => !log.synced);
        
        for (let i = 0; i < remainingToRemove && i < unsyncedLogs.length; i++) {
          await logStore.removeItem(unsyncedLogs[i].id);
        }
      }
      
      this.info(`Purged ${removed} old logs from storage`);
    } catch (error) {
      console.error('Error purging old logs:', error);
    }
  }

  /**
   * Démarre la synchronisation automatique des logs
   */
  startAutoSync() {
    if (this.autoSyncTimer) {
      clearInterval(this.autoSyncTimer);
    }
    
    this.autoSyncTimer = setInterval(() => {
      if (navigator.onLine) {
        this.syncLogs();
      }
    }, this.config.syncInterval * 60 * 1000);
    
    return this;
  }

  /**
   * Arrête la synchronisation automatique des logs
   */
  stopAutoSync() {
    if (this.autoSyncTimer) {
      clearInterval(this.autoSyncTimer);
      this.autoSyncTimer = null;
    }
    
    return this;
  }

  /**
   * Exporte tous les logs actuellement stockés
   */
  async exportLogs() {
    const keys = await logStore.keys();
    const logs = [];
    
    for (const key of keys) {
      const log = await logStore.getItem(key);
      if (log) {
        logs.push(log);
      }
    }
    
    return logs;
  }

  /**
   * Vide tous les logs stockés
   */
  async clearLogs() {
    await logStore.clear();
    this.queue = [];
    this.memoryBuffer = [];
    
    return this;
  }

  // Méthodes de commodité pour les différents niveaux de log

  /**
   * Enregistre un log de niveau DEBUG
   */
  debug(message, data = {}, category = LOG_CATEGORIES.DEFAULT, sendImmediately = true) {
    return this.log(LOG_LEVELS.DEBUG, message, data, category, sendImmediately);
  }

  /**
   * Enregistre un log de niveau INFO
   */
  info(message, data = {}, category = LOG_CATEGORIES.DEFAULT, sendImmediately = true) {
    return this.log(LOG_LEVELS.INFO, message, data, category, sendImmediately);
  }

  /**
   * Enregistre un log de niveau WARNING
   */
  warn(message, data = {}, category = LOG_CATEGORIES.DEFAULT, sendImmediately = true) {
    return this.log(LOG_LEVELS.WARN, message, data, category, sendImmediately);
  }

  /**
   * Enregistre un log de niveau ERROR
   */
  error(message, data = {}, category = LOG_CATEGORIES.DEFAULT, sendImmediately = true) {
    return this.log(LOG_LEVELS.ERROR, message, data, category, sendImmediately);
  }

  /**
   * Enregistre un log de niveau CRITICAL
   */
  critical(message, data = {}, category = LOG_CATEGORIES.DEFAULT, sendImmediately = true) {
    return this.log(LOG_LEVELS.CRITICAL, message, data, category, sendImmediately);
  }

  /**
   * Capture l'exécution d'une fonction et enregistre toute erreur
   */
  async capture(fn, context = 'Unknown operation') {
    try {
      return await fn();
    } catch (error) {
      this.error(`Error in ${context}`, {
        error: {
          message: error.message,
          stack: error.stack,
          name: error.name
        }
      });
      throw error;
    }
  }

  /**
   * Méthodes spécifiques pour les logs de synchronisation
   */
  syncDebug(message, data = {}, sendImmediately = true) {
    return this.debug(message, data, LOG_CATEGORIES.SYNC_DATA, sendImmediately);
  }

  syncInfo(message, data = {}, sendImmediately = true) {
    return this.info(message, data, LOG_CATEGORIES.SYNC_DATA, sendImmediately);
  }

  syncWarning(message, data = {}, sendImmediately = true) {
    return this.warn(message, data, LOG_CATEGORIES.SYNC_DATA, sendImmediately);
  }

  syncError(message, data = {}, sendImmediately = true) {
    return this.error(message, data, LOG_CATEGORIES.SYNC_DATA, sendImmediately);
  }
}

// Créer et exporter une instance unique du service
const logService = new LogService();

// Exporter aussi les constantes pour utilisation dans d'autres fichiers
export { LOG_LEVELS, LOG_CATEGORIES };
export default logService; 